import Connect from 'app/redux/connect'
import Redux from 'app/interface/redux'
import Form from 'components/base/form'
import React, { useEffect, useState } from 'react'
import Overlay from 'react-loading-overlay'
import Input from 'components/base/form/interface/input'
import Basic from 'app/redux/actions/pages/business/basic'
import { businessManagerService } from 'app/service/business-manager'
import { userService } from '@marketing-milk/frontend'
import { FacebookPixel, FBConnectedInstagramAccount, User } from '@marketing-milk/interfaces'
import { Button, Typography } from '@material-ui/core'
import { toast } from 'react-toastify'
import BusinessAuthorizer from './businessAuthorizer'
import ConvertToBuilderDialog from './ConvertToBuilderDialog'

interface FacebookAuthorizersProps extends Redux {
  switchMode: () => void
  new: boolean
}
interface Option {
  readonly label: string
  readonly value: string
}

const FacebookAuthorizers = (props: FacebookAuthorizersProps) => {
  const { main } = props.pages.business
  const [loading, setLoading] = useState(false)
  const [instagram, setInstagram] = useState<FBConnectedInstagramAccount[]>([])
  const [pixelOptions, setPixelOptions] = useState<Option[]>([])
  const [offlineOptions, setOfflineOptions] = useState<Option[]>([])
  const [authorizerUsers, setAuthorizerUsers] = useState<User[]>([])
  const [convertDialogOpen, setConvertDialogOpen] = useState(false)
  const [converting, setConverting] = useState(false)
  const [convertError, setConvertError] = useState<string>()
  const onChange = (key: string, value: any): void => Basic.update(key, value)

  // Page Authorizer is only required if this business actually has a Page set — a business with no
  // Page configured has nothing to convert for that field (see convertAuthorizerToBuilder).
  const hasRequiredAuthorizers =
    !!main.fb_ad_authorizer_id && (!main.fb_page_id || !!main.fb_page_authorizer_id)

  // Snapshot of the authorizer ids as loaded, taken once on mount — this component remounts fresh each
  // time the panel enters Authorizer mode (switchMode), so this always reflects what's actually saved on
  // the business record. Converting reads the authorizer's token off the SAVED business row server-side
  // (@GetBusiness()), not whatever's currently picked in this unsaved form — picking a different
  // authorizer here without saving first and then converting would silently use the old, still-saved
  // authorizer's token instead, which is confusing at best. Comparing against this snapshot lets the
  // button catch that case before it ever calls the backend.
  const [savedAuthorizerIds] = useState({
    ad: main.fb_ad_authorizer_id,
    page: main.fb_page_authorizer_id,
  })
  const hasUnsavedAuthorizerChange =
    main.fb_ad_authorizer_id !== savedAuthorizerIds.ad ||
    main.fb_page_authorizer_id !== savedAuthorizerIds.page

  const canConvert =
    hasRequiredAuthorizers && !!props.pages.business.id && !hasUnsavedAuthorizerChange

  useEffect(() => {
    userService
      .getAll()
      .then(users => setAuthorizerUsers(users.filter(u => u.fbConnected)))
      .catch(() => setAuthorizerUsers([]))
  }, [])

  const authorizerLabel = (user?: User) =>
    user ? `${user.firstName} ${user.lastName} (${user.email})` : undefined
  const adAuthorizerName = authorizerLabel(
    authorizerUsers.find(u => u.id === main.fb_ad_authorizer_id)
  )
  const pageAuthorizerName = authorizerLabel(
    authorizerUsers.find(u => u.id === main.fb_page_authorizer_id)
  )

  const handleConvertConfirm = async () => {
    if (!props.pages.business.id) {
      setConvertError('Save this business before switching to Facebook Builder.')
      return
    }
    setConverting(true)
    setConvertError(undefined)
    try {
      const result = await businessManagerService.convertToBuilder(props.pages.business.id)
      Basic.update('adaccount_system_user_id', result.adAccountSystemUserId)
      Basic.update('fb_account_name', result.adAccountName)
      if (result.pageSystemUserId) Basic.update('page_system_user_id', result.pageSystemUserId)
      if (result.pageName) Basic.update('fb_page_name', result.pageName)
      if (result.datasetName) Basic.update('fb_dataset_name', result.datasetName)
      if (result.offlineName) Basic.update('fb_offline_name', result.offlineName)
      Basic.updateFbBusinessInfo({
        adaccount: result.adAccountBusiness,
        page: result.pageBusiness,
        dataset: result.datasetBusiness,
        offline: result.offlineBusiness,
      })
      // Conversion succeeded server-side and already cleared these on the business row — mirror that
      // here so the panel doesn't keep showing stale authorizers locally until the next full reload.
      Basic.update('fb_ad_authorizer_id', null)
      Basic.update('fb_page_authorizer_id', null)
      if (result.warning) {
        toast.warn(result.warning.message)
      }
      setConvertDialogOpen(false)
      props.switchMode()
    } catch (e: any) {
      setConvertError(
        e?.response?.data?.message ??
          e?.message ??
          'Failed to convert this business to Facebook Builder.'
      )
    } finally {
      setConverting(false)
    }
  }

  const addOfflineOption = (id: string) => {
    setOfflineOptions([...offlineOptions, { value: id, label: `Offline Event Set - ${id}` }])
  }

  useEffect(() => {
    initFetch()
  }, [main.fb_account_id])

  const shouldFetch = () => {
    fetch()
  }

  const initFetch = () => {
    const { fb_account_id } = props.pages.business.main
    if (!!fb_account_id) {
      fetch()
    }
  }

  const fetch = async () => {
    setLoading(true)
    try {
      const results = await fetchFBConnectedAccounts()
      setInstagram(results.instagrams)
      const options = results.pixels.map((p: FacebookPixel) => ({
        value: p.id,
        label: `${p.isDataset ? 'Dataset' : 'Pixel'} - ${p.name} (${p.id})`,
      }))
      setPixelOptions(options)
      setOfflineOptions(
        !options.find(p => p.value === main.offline_event_set_id) && !!main.offline_event_set_id
          ? [
              ...options.filter(o => !o.label.startsWith('Pixel')),
              {
                value: main.offline_event_set_id,
                label: `Offline Event Set - ${main.offline_event_set_id}`,
              },
            ]
          : options.filter(o => !o.label.startsWith('Pixel'))
      )
    } catch (e) {
      console.error('Failed to fetch FB connected accounts', e)
    } finally {
      setLoading(false)
    }
  }

  const currentPixel = () => pixelOptions.find(p => p.value === main.fb_dataset_id)
  const currentOffline = () => offlineOptions.find(p => p.value === main.offline_event_set_id)

  const fetchFBConnectedAccounts = async () => {
    return await businessManagerService.getFBConnectedAccounts(
      `${main.fb_account_id}`,
      props.pages.business.id,
      props.pages.business.main.fb_ad_authorizer_id ?? undefined
    )
  }

  return (
    <div>
      <div className="row px-4">
        <h4 className="col-md-12 px-4">
          <i className="fab fa-facebook"></i>&nbsp;Facebook Authorizers
        </h4>
        <div
          className="col-md-12 mx-4 mb-4 p-4"
          style={{ border: '2px solid #5c6bc0', borderRadius: 8, background: '#eef1fb' }}
        >
          <Typography
            variant="overline"
            style={{ color: '#3949ab', fontWeight: 700, letterSpacing: 1 }}
          >
            Beta
          </Typography>
          <h4 style={{ marginTop: 4 }}>Switch to Facebook Builder</h4>
          <p className="mb-3">
            Converting attaches a system user using the authorizers' current permissions — going
            forward, changes depend on <strong>your own</strong> Facebook permissions instead. This
            changes how Marketing Milk connects to this business's Facebook assets; you'll confirm
            before anything happens.
          </p>
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
            {props.new ? (
              <Button
                data-testid="manual-builder-setup-btn"
                variant="contained"
                style={{ background: '#3949ab', color: '#fff' }}
                size="large"
                onClick={() => props.switchMode()}
              >
                <span>Connect a System User Manually</span>
              </Button>
            ) : (
              <Button
                data-testid="convert-to-builder-btn"
                variant="contained"
                style={canConvert ? { background: '#3949ab', color: '#fff' } : undefined}
                size="large"
                disabled={!canConvert}
                onClick={() => setConvertDialogOpen(true)}
              >
                <span>Convert Authorizers to System User</span>
              </Button>
            )}
          </div>
          {!props.new &&
            (!hasRequiredAuthorizers ? (
              <p className="text-muted mt-2 mb-0">
                <small>
                  {main.fb_page_id
                    ? 'An Ad Account Authorizer and a Page Authorizer must be set first.'
                    : 'An Ad Account Authorizer must be set first.'}
                </small>
              </p>
            ) : !props.pages.business.id ? (
              <p className="text-muted mt-2 mb-0">
                <small>Save this business before switching to Facebook Builder.</small>
              </p>
            ) : (
              hasUnsavedAuthorizerChange && (
                <p className="text-muted mt-2 mb-0">
                  <small>
                    You've changed an authorizer above without saving yet. Save this business first,
                    or refresh the page to discard the change, before switching to Facebook Builder.
                  </small>
                </p>
              )
            ))}
          {convertError && !convertDialogOpen && (
            <p className="text-danger mt-2 mb-0">
              <small>{convertError}</small>
            </p>
          )}
        </div>
        <div className="row col-md-12 pb-4">
          <div className="col-md-6 col-sm-12 col-xs-12">
            <BusinessAuthorizer
              title={'Ad Account Authorizer'}
              business={props.pages.business}
              authorizerID={main.fb_ad_authorizer_id}
              assignAuthorizer={(authorizer: User) => {
                onChange('fb_ad_authorizer_id', authorizer.id)
              }}
              popover={
                'Facebook Ad Account Authorizers must be added to the Facebook Ad Account as an Account Admin'
              }
            />
          </div>
          <div className="col-md-6 col-sm-12 col-xs-12">
            <BusinessAuthorizer
              title={'Page Authorizer'}
              business={props.pages.business}
              authorizerID={main.fb_page_authorizer_id}
              assignAuthorizer={(authorizer: User) => {
                onChange('fb_page_authorizer_id', authorizer.id)
              }}
              popover={
                'Facebook Page Authorizers must be added to the Facebook Page as a Page Admin and have Default Leads Access'
              }
            />
          </div>
        </div>
      </div>
      <ConvertToBuilderDialog
        open={convertDialogOpen}
        adAuthorizerName={adAuthorizerName}
        pageAuthorizerName={pageAuthorizerName}
        hasPage={!!main.fb_page_id}
        converting={converting}
        error={convertError}
        onConfirm={handleConvertConfirm}
        onCancel={() => {
          setConvertDialogOpen(false)
          setConvertError(undefined)
        }}
      />
      <Overlay
        active={loading}
        spinner
        text="Fetching information about this business' ad account"
        className="pb-5"
      >
        <Form
          save={false}
          onChange={(col, label) => {
            Basic.update(col, label)
            if (col === 'use_offline_id') {
              Basic.update('offline_event_set_id', '')
            }
          }}
          onSubmit={() => null}
          groups={[
            {
              header: 'Facebook',
              rows: [
                {
                  column: 6,
                  fields: [
                    {
                      column: 'fb_account_id',
                      text: 'Account ID',
                      type: 'regular',
                      value: main.fb_account_id || '',
                      onBlur: shouldFetch,
                    } as Input,
                    {
                      column: 'fb_page_id',
                      text: 'Page ID',
                      type: 'regular',
                      value: main.fb_page_id || null,
                    } as Input,
                  ],
                },
                {
                  column: 6,
                  fields: [
                    {
                      column: 'instagram_id',
                      text: 'Instagram Page',
                      type: 'select',
                      options: instagram,
                      value:
                        instagram.find((x: any) => x.id === main.instagram_id) ?? {
                          id: main.instagram_id,
                        } ??
                        null,
                      selector: {
                        getOptionLabel: x => x.username ?? x.id,
                        getOptionValue: x => x.id,
                        value: 'id',
                        clearable: true,
                      },
                      onChange: (column: string, value: any) => Basic.update(column, value.id),
                    } as Input,
                  ],
                },
              ],
            },
            {
              header: 'Facebook Datasets',
              rows: [
                {
                  column: 6,
                  fields: [
                    {
                      column: 'fb_dataset_id',
                      text: 'Dataset ID',
                      type: 'select',
                      options: pixelOptions,
                      value: pixelOptions.find((x: Option) => x.value === main.fb_dataset_id) ?? {
                        value: main.fb_dataset_id,
                      },
                    } as Input,
                    {
                      type: 'toggle',
                      column: 'use_offline_id',
                      text: 'Use Separate Dataset for Offline Data?',
                      value: main.use_offline_id,
                    } as Input,
                  ],
                },
                {
                  column: 6,
                  fields: [
                    {
                      column: 'offline_event_set_id',
                      text: 'Offline ID',
                      type: 'select_create',
                      disabled: !main.use_offline_id,
                      options: offlineOptions,
                      value: currentOffline(),
                      onCreate: (value: string) => {
                        addOfflineOption(value)
                        Basic.update('offline_event_set_id', value)
                      },
                    } as Input,
                  ],
                },
              ],
            },
          ]}
        />
        <div className="col-sm-12">
          {((!main.offline_event_set_id && currentPixel()?.label.startsWith('Pixel')) ||
            (!!main.offline_event_set_id && currentOffline()?.label.startsWith('Pixel'))) && (
            <div className="alert alert-outline-danger alert-dismissible fade show" role="alert">
              <strong>Please Note</strong>&nbsp; Uploads will not work. The ID you are uploading
              offline data to is a Pixel. It must be a Dataset or Offline Event Set in order to
              properly upload Data. Please either provide a proper Dataset or Offline Event Set ID
            </div>
          )}
          {!!main.offline_event_set_id && currentOffline()?.label.startsWith('Offline') && (
            <div className="alert alert-outline-danger alert-dismissible fade show" role="alert">
              <strong>Please Note</strong>&nbsp; Uploads will not work. You are using an Offline
              Event Set that uses the Offline Conversions API, which is deprecated. Please convert
              this Offline Event Set to a Dataset on Facebook and use that instead, or simply use
              the Dataset ID field for both online and offline data.
            </div>
          )}
          <p className="text-default">
            With the current configuration, this business will upload <strong>offline data</strong>{' '}
            via the{' '}
            <strong
              className={
                currentOffline()?.label.startsWith('Offline') ? 'text-danger' : 'text-info'
              }
            >
              {currentOffline()?.label.startsWith('Offline') ? 'Offline ' : ''} Conversions API
            </strong>
            &nbsp; to the{' '}
            <strong
              className={
                currentOffline()?.label.startsWith('Offline') ? 'text-danger' : 'text-info'
              }
            >
              {currentOffline()?.label ?? currentPixel()?.label}
            </strong>{' '}
            and pull
            <strong> online data</strong> from the <strong>{currentPixel()?.label}</strong>.
          </p>
        </div>
        <div className="col-sm-12 pb-5">
          <p className="text-info">
            By default, the system will upload offline data to the Dataset ID selected above, as
            well as pull online data from that Dataset. If you want to use a separate Dataset for
            offline data, check the box above and select the Offline ID from the dropdown.
          </p>
        </div>
      </Overlay>
    </div>
  )
}

export default Connect(FacebookAuthorizers)
